import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Assessment() {
  const [questions, setQuestions] = useState([]);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  // Mount-only fetch. `api` is a stable module import; setters are stable React refs.
  useEffect(() => {
    (async () => {
      const [{ data: q }, { data: s }] = await Promise.all([
        api.get("/assessment/questions"),
        api.get("/assessment/status"),
      ]);
      // Shuffle: prevents high-weightage answers from clustering at the top.
      // Fisher-Yates with a fresh seed per session; user can still Back/Next within their attempt.
      const shuffled = [...(q.questions || [])];
      for (let j = shuffled.length - 1; j > 0; j--) {
        const k = Math.floor(Math.random() * (j + 1));
        [shuffled[j], shuffled[k]] = [shuffled[k], shuffled[j]];
      }
      setQuestions(shuffled);
      if (s.answers) setAnswers(s.answers);
    })();
  }, []);

  if (!questions.length) return <div className="p-10 text-stone-500">Loading assessment…</div>;
  const q = questions[i];
  const total = questions.length;
  const progress = Math.round(((i + 1) / total) * 100);
  const isLast = i === total - 1;

  const choose = (opt) => setAnswers({ ...answers, [q.id]: opt });

  const next = async () => {
    if (!answers[q.id]) { toast.error("Please choose an option"); return; }
    if (!isLast) { setI(i + 1); return; }
    setSubmitting(true);
    try {
      await api.post("/assessment/submit", { answers });
      await refresh();
      toast.success("Assessment complete. See your results.");
      nav("/results");
    } catch { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  let nextLabel = "Next";
  if (isLast) nextLabel = submitting ? "Submitting…" : "Finish";

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-2">
          <span className="overline">Question {i + 1} of {total}</span>
          <span className="text-xs text-stone-500">{q.dimension}</span>
        </div>
        <div className="h-1 rounded-full bg-stone-200 overflow-hidden mb-10">
          <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={q.id}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}>
            <h2 data-testid="assessment-question" className="font-serif-display text-3xl lg:text-4xl text-[#1E1B4B] leading-tight">{q.text}</h2>
            <div className="mt-10 space-y-3">
              {q.options.map((opt) => {
                const active = answers[q.id] === opt;
                return (
                  <button key={opt} data-testid={`assessment-option-${opt.slice(0, 12).replace(/\s+/g, "-")}`}
                    onClick={() => choose(opt)}
                    className={`w-full text-left px-6 py-5 rounded-2xl border transition-colors duration-200 ${active ? "bg-amber-50 border-amber-500 text-[#1E1B4B]" : "bg-white border-stone-200 hover:border-[#1E1B4B] text-stone-700"}`}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex justify-between items-center">
          <button data-testid="assessment-prev-btn" disabled={i === 0} onClick={() => setI(Math.max(0, i - 1))}
            className="btn-pill text-stone-600 border border-stone-300 hover:bg-white disabled:opacity-30 flex items-center gap-1"><ChevronLeft size={16} /> Back</button>
          <button data-testid="assessment-next-btn" onClick={next} disabled={submitting}
            className="btn-pill text-white flex items-center gap-1" style={{ background: "#1E1B4B" }}>
            {nextLabel} <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
